<template>
  <div class="client-form" :class="{ history: isHistoryRecord }">
    <form class="vertical">
      <section>
        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('client.labels.clientName') }}</label>
            <InputField
              v-model="formData.name"
              type="text"
              class="bordered"
              @change="formUpdated"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('client.labels.clientType') }}</label>
            <DropdownSelect
              :menu-items="clientTypeOptions"
              :selected-item="getDisplayClientType"
              class="bordered"
              @select-item="onClientTypeChange"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('client.labels.phoneNumber') }}</label>
            <InputField
              v-model="formData.phone"
              type="text"
              class="bordered"
              field-icon="phone-square"
              @change="formUpdated"
            />
          </div>
        </div>
        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('client.labels.creditCheckDate') }}</label>
            <FormDatePicker
              v-model="creditCheckDate"
              type="text"
              class="'bordered'"
              @change="onCreditCheckDateChange"
            />
          </div>
          <div class="form-control">
            <label>{{ $t('client.labels.paymentTerms') }}</label>
            <InputField
              v-model="formData.payment_terms"
              type="text"
              class="bordered"
              @input="formUpdated"
            />
          </div>
          <div class="form-control term">
            <label>{{ $t('client.labels.paymentTermUnit') }}</label>
            <DropdownSelect
              :menu-items="termUnits"
              :selected-item="paymentTermUnit"
              class="bordered"
              @select-item="
                formData.payment_term_unit = $event;
                checkPaymentTerms();
              "
            />
          </div>
        </div>

        <div class="form-control">
          <label>{{ $t('client.labels.additionalComment') }}</label>
          <TolTextArea v-model="formData.additional_comment" @change="formUpdated" />
        </div>
        <div class="form-group">
          <div class="form-control">
            <label>{{ $t('client.labels.assignmentRequirement') }}</label>
            <Checkbox
              size="tiny"
              :checked="formData.requires_purchase_order"
              @change="onAssignmentRequirementChange"
            />
          </div>

          <div class="form-control">
            <label>{{ $t('client.labels.clientProfile') }}</label>
            <Checkbox size="tiny" :checked="formData.active" @change="OnClientProfileTypeChange" />
          </div>
        </div>
      </section>
    </form>
  </div>
</template>
<script>
import InputField from '@/app/components/InputField';
import Checkbox from '@/app/components/Checkbox';
import TolTextArea from '@/app/components/TolTextArea';
import DropdownSelect from '@/app/components/DropdownSelect';
import FormDatePicker from '@/app/components/FormDatePicker';
import { TolErrorsGetters } from '@/app/features/tol-errors/tol-errors.keys';
import { dateUtils } from '@/mixins/date-utils';
import format from 'date-fns/format';
import * as R from 'ramda';
import { ClientDetailsActions } from '@/app/features/clients/features/client-details/client-details.keys';
export default {
  components: {
    InputField,
    DropdownSelect,
    FormDatePicker,
    TolTextArea,
    Checkbox
  },
  mixins: [dateUtils],
  props: {
    clientDetails: {
      type: Object,
      required: true
    },
    isHistoryRecord: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOnScreenDataModified: false,
      isProcessBtnClicked: false,
      formData: {},
      clientTypeOptions: ['Regular', 'End Client', 'Both'],
      clientProfileType: ['Active', 'Inactive'],
      clientAssignmentRequirementStatus: ['Yes', 'No'],
      contactsColumns: ['referenceNumber', 'firstName', 'lastName', 'email', 'phone1']
    };
  },
  computed: {
    getDisplayClientType() {
      let clientType = '';
      if (this.formData.type === 'REGULAR') {
        clientType = 'Regular';
      } else if (this.formData.type === 'END_CLIENT') {
        clientType = 'End Client';
      } else {
        clientType = 'Both';
      }
      return clientType;
    },
    creditCheckDate() {
      return this.formData.credit_check ? this.dateFromString(this.formData.credit_check) : '';
    },
    clientProfileStatus() {
      return this.formData.active ? 'Active' : 'Inactive';
    },
    clientAssignmentRequirement() {
      return this.formData.requires_purchase_order ? 'Yes' : 'No';
    },
    currentSaveStatus() {
      return this.$store.getters[TolErrorsGetters.HasErrors] ? 'error' : 'success';
    },
    isNewClient() {
      return !this.formData.reference_number;
    },
    paymentTermUnit() {
      return this.formData.payment_term_unit ? this.formData.payment_term_unit : 'N/A';
    },
    termUnits() {
      // TODO: implement AppGetters.TermUnits
      // return this.$store.getters[AppGetters.TermUnits];
      return ['DAYS', 'WEEKS', 'MONTHS'];
    }
  },
  watch: {
    clientDetails: function() {
      this.formData = R.clone(this.clientDetails);
    }
  },
  updated() {
    if (this.isHistoryRecord && !this.areFormFieldsDisabled) {
      const forms = document.querySelectorAll('.client-form.history form');

      forms.forEach(form => this.disableFormElements(form));
      this.areFormFieldsDisabled = true;
    }
  },
  mounted() {
    this.formData = R.clone(this.clientDetails);
  },
  methods: {
    formUpdated() {
      this.isOnScreenDataModified = true;
      this.$emit('formDataChange', this.formData);
      this.$store.dispatch(ClientDetailsActions.UpdateClientDetails, this.formData);
    },
    formatDate(date) {
      return date ? format(date, 'YYYY-MM-DD') : null;
    },
    onClientTypeChange(event) {
      event = event === 'End Client' ? 'END_CLIENT' : event.toUpperCase();
      this.formData.type = event;
      this.formUpdated();
    },
    onCreditCheckDateChange(event) {
      if (!this.isHistoryRecord) {
        this.formData.credit_check = this.formatDate(event);
        this.formUpdated();
      }
    },
    OnClientProfileTypeChange(event) {
      this.formData.active = event.checked;
      this.formUpdated();
    },
    onAssignmentRequirementChange(event) {
      this.formData.requires_purchase_order = event.checked;
      this.formUpdated();
    },
    disableFormElements(form) {
      for (let i = 0; i < form.elements.length; i++) {
        form.elements[i].disabled = true;
      }
      const dropDowns = form.querySelectorAll('.dropdown-container');
      dropDowns.forEach(dropdown => dropdown.setAttribute('disabled', 'true'));
    },
    checkPaymentTerms() {}
  }
};
</script>
<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.client-form {
  &.history {
    font-size: 0.8em;
    background-color: $light-grey-2;
    padding: 25px;
  }
  form {
    width: 100%;

    section {
      margin-bottom: 20px;
      background-color: $background-level-3;

      h1 {
        padding-top: 15px;
        border-bottom: 1px solid $row-border;
      }
      h1,
      h2 {
        flex: 0 100%;
      }

      .break {
        width: 100%;
        height: 0;
      }
      .form-group {
        display: flex;
      }
      .form-control {
        padding: 10px 0;
        margin: 1em;
        flex: 1;
        > label {
          display: block;
          font-weight: bold;
        }

        .multi-inline {
          display: flex;
          justify-content: flex-start;
          align-items: flex-end;

          > * {
            margin-left: 1em;
          }
          > *:first-child {
            margin-left: 0;
          }
          .dropdown-container {
            width: 30%;
          }
        }
      }
    }

    .form-input {
      border-bottom: 1px solid $blue;
      font-size: 1em;
      height: inherit;
      &:focus {
        outline: none;
      }
      &.error {
        border-bottom: 1px solid $red;
      }

      &.small {
        width: auto;
        display: inline-flex;
        margin-right: 15px;
      }
    }

    .bordered {
      border: 1px solid $blue;
      font-size: 1em;
      &:focus {
        outline: none;
      }
    }
  }

  .action-block {
    width: 100%;
    margin-left: 90%;
    margin-top: 5px;
  }

  .client-action {
    width: 50px;
    height: 50px;
    padding: 0;
    font-size: 1.5em;
    margin-right: 20px;
    color: $white;
    border-radius: 100%;
    background-color: $secondary-green;

    &.disabled {
      background-color: $light-grey-2;
      color: $light-grey-1;
      &:hover {
        background-color: $light-grey-2;
        color: $light-grey-1;
        cursor: not-allowed;
      }
    }
  }

  &.modified {
    border-left: 5px solid $orange;
  }

  .error {
    color: $red;
  }
}
</style>
